import { createIdempotencyKey, generateFingerprint } from '@peanuds/shared';

import type {
  DraftFingerprint,
  ExternalTransferDraft,
  InternalTransferDraft,
  SendMoneyDraft,
} from './send-money.types';
import type { RecipientTransferRouting } from './send-money.routing';

export const generateDraftFingerprint = (
  draft: SendMoneyDraft,
  recipientRouting: RecipientTransferRouting | null = null,
): DraftFingerprint => {
  const parts: string[] = [];
  const normalizedDescription = normalizeString(draft.description);

  parts.push(`flow:${draft.flowType}`);
  parts.push(`kind:${draft.transferKind}`);
  parts.push(`source:${draft.sourceAccountId ?? 'null'}`);
  parts.push(`amount:${draft.amount ?? 'null'}`);
  parts.push(`date:${draft.transferDate ?? 'null'}`);
  parts.push(`desc:${normalizedDescription ?? 'null'}`);

  if (draft.transferKind === 'external') {
    const normalizedCustomReason = normalizeString(draft.transferCustomReason);

    parts.push(`beneficiary:${draft.beneficiaryId ?? 'null'}`);
    parts.push(`bankAccount:${draft.beneficiaryBankAccountId ?? 'null'}`);
    parts.push(`scheme:${draft.scheme ?? 'null'}`);
    parts.push(`reason:${draft.transferReasonId ?? 'null'}`);
    parts.push(`customReason:${normalizedCustomReason ?? 'null'}`);

    if (recipientRouting !== null) {
      parts.push(`routing:${recipientRouting.routing}`);
      if (recipientRouting.routing === 'internal') {
        parts.push(`internalDestination:${recipientRouting.internalDestinationAccountId}`);
      }
    }
  } else {
    parts.push(`destination:${draft.destinationAccountId ?? 'null'}`);
    if (
      draft.beneficiaryId !== undefined ||
      draft.beneficiaryBankAccountId !== undefined ||
      draft.transferReasonId !== undefined ||
      draft.transferCustomReason !== undefined
    ) {
      parts.push(`beneficiary:${draft.beneficiaryId ?? 'null'}`);
      parts.push(`bankAccount:${draft.beneficiaryBankAccountId ?? 'null'}`);
      parts.push(`reason:${draft.transferReasonId ?? 'null'}`);
      parts.push(`customReason:${normalizeString(draft.transferCustomReason) ?? 'null'}`);
    }
  }

  return generateFingerprint(parts);
};

export const generateIdempotencyKey = (): string => {
  return createIdempotencyKey();
};

export const isExternalDraft = (draft: SendMoneyDraft): draft is ExternalTransferDraft =>
  draft.transferKind === 'external';

export const isInternalDraft = (draft: SendMoneyDraft): draft is InternalTransferDraft =>
  draft.transferKind === 'internal';

export const normalizeString = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
